// src/shared-components/cards/components/ArtistSliderCard.jsx
import { useMemo, useState } from "react";

import useTranslations from "@/i18n/useTranslations";
import ArtworksOfArtist from "@/pages/profile/artists/artworks-of-artist";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import ArtworkImage   from "@/shared-components/cards/components/ArtworkImage";
import UserProfileImage from "@/shared-components/cards/components/UserProfileImage";
import Text           from "@/shared-components/text/components/Text";

import "./styles/ArtistSliderCard.css";

export default function ArtistSliderCard({ artist, onVisit }) {
  const tHome = useTranslations('common.shared.home');
  const [openArtworksModal, setOpenArtworksModal] = useState(false);

  // Normaliza ID y user_type para decidir navegación vs modal
  const normalizedArtist = useMemo(() => {
    const id = artist?._id ?? artist?.id ?? null;
    const userType = artist?.user_type ?? (artist?.username || artist?.user_username ? "artist" : "gallery_artist");
    return { ...artist, _id: id, user_type: userType };
  }, [artist]);

  const galleryArtistId = normalizedArtist?._id;
  const artworksEndpoint = galleryArtistId ? `/galleryartist/${galleryArtistId}/artworks` : null;
  const cacheKey = galleryArtistId ? `artworks_of_${galleryArtistId}_in_search` : null;
  const isClaimedArtist =
    normalizedArtist.user_type === "artist" &&
    Boolean(normalizedArtist.username || normalizedArtist.user_username);
  const desktopButtonLabel = isClaimedArtist ? tHome('visit_profile') : "See profile";
  const mobileButtonLabel = isClaimedArtist ? "Visit" : "See";

  const handleVisit = () => {
    // Claimed artists go to profile; unclaimed open artworks modal
    if (isClaimedArtist) {
      onVisit?.();
    } else if (galleryArtistId) {
      setOpenArtworksModal(true);
    }
  };

  const coverImage =
    artist.coverImage ||
    artist.cover_image ||
    artist.artist_cover_image?.image_id ||
    null;
  const imageId = coverImage || artist.image;
  const claimedProfileImage =
    artist.profilePic ||
    artist.profile_pic ||
    artist.user_image?.image_id ||
    artist.user_image ||
    artist.artist_image?.image_id ||
    null;
  const isClaimedProfile =
    normalizedArtist.user_type === "artist" &&
    Boolean(claimedProfileImage);
  const aspectRatio =
    artist.coverAspectRatio ||
    artist.cover_aspect_ratio ||
    artist.artist_cover_image?.image_original_data?.aspect_ratio ||
    3 / 2;

  return (
    <>
      <div className="artist-card__container" onClick={handleVisit}>
        <div className="artist-card__image-wrapper">
          {isClaimedProfile ? (
            <div className="artist-card__avatar">
              <UserProfileImage
                image={claimedProfileImage}
                typeOfProfile="artist"
                className="artist-card__avatar-image"
              />
            </div>
          ) : null}
          {imageId ? (
            <ArtworkImage
              image={imageId}
              imageClassName="artist-card__image"
              imageTemplateClassName="artist-card__image"
              style={{ aspectRatio }}
            />
          ) : (
            <div className="artist-card__no-image" aria-hidden="true" />
          )}
        </div>

        <div className="artist-card__info-row">
          <div className="artist-card__info-left">
            <Text
              className="profile-info__user_name dots-on-overflow"
              paragraph>
              {artist.artist_name}
            </Text>
            {artist.country && (
              <Text
                className="artwork-label__artist-personal dots-on-overflow">
                {artist.country}
                {artist.birth ? `, ${artist.birth}` : ""}
              </Text>
            )}
          </div>
          <ContinueButton
            className="artist-card__button"
            onClick={(e) => { e.stopPropagation(); handleVisit(); }}
          >
            <span className="artist-card__button-text--desktop">
              {desktopButtonLabel}
            </span>
            <span className="artist-card__button-text--mobile">
              {mobileButtonLabel}
            </span>
          </ContinueButton>
        </div>
      </div>
      {openArtworksModal && (
        <ArtworksOfArtist
          open={openArtworksModal}
          close={() => setOpenArtworksModal(false)}
          artist={normalizedArtist}
          internal={false}
          showHeader={false}
          cacheKey={cacheKey}
          queryEndpoint={artworksEndpoint}
        />
      )}
    </>
  );
}
